const resolveStickyBuyVisibility = (entry, hasObservedMainButton) => {
  const intersectionWidth = entry.intersectionRect?.width || 0;
  const intersectionHeight = entry.intersectionRect?.height || 0;
  const hasVisibleIntersection =
    entry.isIntersecting || entry.intersectionRatio > 0 || (intersectionWidth > 0 && intersectionHeight > 0);
  const hasObservedButton = hasObservedMainButton || hasVisibleIntersection;
  const rootTop = entry.rootBounds?.top || 0;
  const isFullyAboveViewport = entry.boundingClientRect.bottom <= rootTop;

  return {
    hasObservedButton,
    visible: hasObservedButton && !hasVisibleIntersection && isFullyAboveViewport,
  };
};

const resolveVisualViewportOffset = (viewport, layoutViewportHeight) => {
  if (!viewport) return 0;
  return Math.max(0, Math.round(layoutViewportHeight - viewport.height - viewport.offsetTop));
};

const formatPredictiveSearchPrice = (value, format, currency, locale) => {
  if (value == null || String(value).trim() === '') return '';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '';
  const formats = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_space_separator: [0, ' ', '.'],
    amount_with_space_separator: [2, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.']
  };
  const placeholder = String(format || '').match(/\{\{\s*(\w+)\s*\}\}/);
  const specification = placeholder && formats[placeholder[1]];
  if (Array.isArray(specification)) {
    const [precision, thousands, decimal] = specification;
    const [integer, fraction] = amount.toFixed(precision).split('.');
    const formatted = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    return format.replace(placeholder[0], () => formatted);
  }
  if (!currency) return '';
  try {
    return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return '';
  }
};

const stickyBuyControllers = new WeakMap();
const visibleStickyBuys = new Set();

class YevoriEnhancements {
  constructor() {
    this.bindFormFeedback();
    this.bindFirstOrderPopup();
    this.bindPredictiveSearch();
    this.bindOptionPills();
    this.bindStickyProductBuy();
  }

  getSubmittedFeedbackForm() {
    let formId;
    try {
      formId = decodeURIComponent(window.location.hash.slice(1));
    } catch {
      return null;
    }
    const submittedForm = document.getElementById(formId);
    return submittedForm?.matches('[data-feedback-form]') && submittedForm.querySelector('[data-form-feedback]')
      ? submittedForm
      : null;
  }

  bindFormFeedback() {
    const submittedForm = this.getSubmittedFeedbackForm();
    if (!submittedForm) return;
    const feedback = submittedForm.querySelector('[data-form-feedback]');
    document.querySelectorAll('[data-feedback-form]').forEach((form) => {
      form.querySelectorAll('[data-form-feedback]').forEach((message) => {
        message.hidden = form !== submittedForm;
      });
    });
    if (!submittedForm.closest('[data-first-order-popup]')) feedback.focus();
  }

  bindFirstOrderPopup() {
    const popup = document.querySelector('[data-first-order-popup]');
    if (!popup || popup.dataset.popupBound === 'true') return;
    if (new URLSearchParams(window.location.search).has('hide_offer_popup')) return;
    const feedbackForm = popup.querySelector('[data-feedback-form]');
    const submittedForm = this.getSubmittedFeedbackForm();
    if (submittedForm && submittedForm !== feedbackForm) return;
    const returnedToPopup = feedbackForm && submittedForm === feedbackForm;
    const feedback = returnedToPopup && feedbackForm.querySelector('[data-form-feedback]');

    const storageKey = 'yevori-first-order-popup-dismissed';
    const frequencyDays = Number(popup.dataset.popupFrequency || 7);
    const delay = Number(popup.dataset.popupDelay || 0) * 1000;
    let lastDismissed = 0;

    try {
      lastDismissed = Number(window.localStorage.getItem(storageKey) || 0);
    } catch (error) {
      lastDismissed = 0;
    }

    const frequencyMilliseconds = frequencyDays * 24 * 60 * 60 * 1000;
    if (!feedback && lastDismissed && Date.now() - lastDismissed < frequencyMilliseconds) return;
    popup.dataset.popupBound = 'true';
    let returnFocus = null;

    const closePopup = () => {
      popup.hidden = true;
      document.body.classList.remove('promo-popup-open');
      if (returnFocus?.isConnected) returnFocus.focus();
      try {
        window.localStorage.setItem(storageKey, String(Date.now()));
      } catch (error) {
        // Storage can be unavailable in privacy-restricted browsers.
      }
    };

    popup.querySelectorAll('[data-popup-close]').forEach((button) => {
      button.addEventListener('click', closePopup);
    });

    popup.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePopup();
      }
      if (event.key === 'Tab') {
        const focusable = Array.from(popup.querySelectorAll('button, input, a[href], [tabindex="0"]'))
          .filter((element) => !element.disabled && !element.hidden && !element.classList.contains('first-order-popup__backdrop'));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !focusable.includes(document.activeElement))) {
          event.preventDefault();
          first?.focus();
        }
      }
    });

    window.setTimeout(() => {
      if (!feedback && (window.aureliaTheme?.activeDrawer || document.querySelector('dialog[open]'))) return;
      returnFocus = document.activeElement;
      popup.hidden = false;
      document.body.classList.add('promo-popup-open');
      (feedback || popup.querySelector('[data-popup-close]:not(.first-order-popup__backdrop)'))?.focus();
    }, feedback ? 0 : delay);
  }

  bindPredictiveSearch() {
    document.querySelectorAll('[data-predictive-search]').forEach((form) => {
      const input = form.querySelector('[data-predictive-search-input]');
      const results = form.parentElement.querySelector('[data-predictive-search-results]');
      const baseUrl = form.dataset.predictiveUrl;
      if (!input || !results || !baseUrl || form.dataset.predictiveBound === 'true') return;
      form.dataset.predictiveBound = 'true';

      let timer;
      let controller;
      let requestVersion = 0;

      const closeResults = () => {
        requestVersion += 1;
        window.clearTimeout(timer);
        controller?.abort();
        results.hidden = true;
        results.replaceChildren();
      };

      const addStatus = (message) => {
        const status = document.createElement('p');
        status.className = 'predictive-search__status';
        status.setAttribute('role', 'status');
        status.textContent = message;
        results.replaceChildren(status);
        results.hidden = false;
      };

      const renderResults = (products, query) => {
        results.replaceChildren();

        if (!products.length) {
          addStatus(form.dataset.noResultsLabel || 'No matching pieces found.');
          return;
        }

        products.forEach((product) => {
          const link = document.createElement('a');
          link.className = 'predictive-search__item';
          link.href = product.url;

          const media = document.createElement('span');
          media.className = 'predictive-search__image';
          if (product.featured_image?.url) {
            const image = document.createElement('img');
            const imageUrl = new URL(product.featured_image.url, window.location.origin);
            imageUrl.searchParams.set('width', '180');
            image.src = imageUrl.toString();
            image.alt = product.featured_image.alt || product.title;
            image.loading = 'lazy';
            image.width = 90;
            image.height = 90;
            media.append(image);
          }

          const copy = document.createElement('span');
          const title = document.createElement('span');
          title.className = 'predictive-search__title';
          title.textContent = product.title;
          const price = document.createElement('span');
          price.className = 'predictive-search__price';
          price.innerHTML = formatPredictiveSearchPrice(product.price, form.dataset.moneyFormat, form.dataset.currencyCode, form.dataset.locale);
          copy.append(title, price);
          link.append(media, copy);
          results.append(link);
        });

        const allResults = document.createElement('a');
        allResults.className = 'predictive-search__all';
        allResults.href = `${form.action}?q=${encodeURIComponent(query)}&type=product`;
        allResults.textContent = (form.dataset.allResultsLabel || 'View all results for “__query__”').replace('__query__', query);
        results.append(allResults);
        results.hidden = false;
      };

      const search = async () => {
        const query = input.value.trim();
        if (query.length < 2) {
          closeResults();
          return;
        }

        controller?.abort();
        controller = new AbortController();
        const currentVersion = ++requestVersion;
        addStatus(form.dataset.searchingLabel || 'Searching…');

        const url = `${baseUrl}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=hide`;

        try {
          const response = await fetch(url, {
            headers: { Accept: 'application/json' },
            signal: controller.signal
          });
          if (!response.ok) throw new Error('Predictive search failed');
          const payload = await response.json();
          if (currentVersion !== requestVersion || query !== input.value.trim() || form.closest('[data-drawer]')?.hidden) return;
          const products = payload?.resources?.results?.products || [];
          renderResults(products, query);
        } catch (error) {
          if (error.name !== 'AbortError' && currentVersion === requestVersion && query === input.value.trim() && !form.closest('[data-drawer]')?.hidden) {
            addStatus(form.dataset.errorLabel || 'Search suggestions are temporarily unavailable. Press Enter to view all results.');
          }
        }
      };

      input.addEventListener('input', () => {
        closeResults();
        timer = window.setTimeout(search, 180);
      });

      form.closest('[data-drawer]')?.addEventListener('yevori:drawer-close', closeResults);

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeResults();
      });
    });
  }

  bindStickyProductBuy(container = document) {
    const roots = container.matches?.('[data-product-root]')
      ? [container]
      : Array.from(container.querySelectorAll('[data-product-root]'));

    roots.forEach((root) => {
      const sticky = root.querySelector('[data-product-sticky-buy]');
      const mainButton = root.querySelector('[data-add-to-cart]');
      const form = mainButton?.closest('form');
      if (!sticky || !mainButton || !form || stickyBuyControllers.has(root)) return;

      const stickyButton = sticky.querySelector('[data-sticky-add]');
      if (!stickyButton) return;
      sticky.dataset.stickyBuyBound = 'true';
      let hasObservedMainButton = false;

      const setVisible = (visible) => {
        sticky.classList.toggle('is-visible', visible);
        if (visible) {
          visibleStickyBuys.add(sticky);
        } else {
          visibleStickyBuys.delete(sticky);
        }
        document.body.classList.toggle('has-sticky-buy', visibleStickyBuys.size > 0);
      };

      const updateMeasurements = () => {
        const stickyHeight = Math.ceil(sticky.getBoundingClientRect().height);
        document.body.style.setProperty('--sticky-buy-height', `${stickyHeight}px`);

        const viewport = window.visualViewport;
        const layoutViewportHeight = Math.max(
          window.innerHeight,
          document.documentElement.clientHeight
        );
        const viewportOffset = resolveVisualViewportOffset(viewport, layoutViewportHeight);
        document.body.style.setProperty('--sticky-buy-viewport-offset', `${viewportOffset}px`);
      };

      const submitMainForm = () => {
        if (mainButton.disabled || stickyButton.disabled || form.dataset.addState === 'pending' || form.dataset.addState === 'added') return;
        form.requestSubmit(mainButton);
      };
      stickyButton.addEventListener('click', submitMainForm);

      const observer = new IntersectionObserver(
        ([entry]) => {
          const state = resolveStickyBuyVisibility(entry, hasObservedMainButton);
          hasObservedMainButton = state.hasObservedButton;
          setVisible(state.visible);
          updateMeasurements();
        },
        { threshold: [0, 0.01, 1] }
      );
      observer.observe(mainButton);

      const resizeObserver = new ResizeObserver(updateMeasurements);
      resizeObserver.observe(sticky);
      const viewport = window.visualViewport;
      viewport?.addEventListener('resize', updateMeasurements, { passive: true });
      viewport?.addEventListener('scroll', updateMeasurements, { passive: true });

      stickyBuyControllers.set(root, {
        destroy() {
          observer.disconnect();
          resizeObserver.disconnect();
          stickyButton.removeEventListener('click', submitMainForm);
          viewport?.removeEventListener('resize', updateMeasurements);
          viewport?.removeEventListener('scroll', updateMeasurements);
          setVisible(false);
          sticky.removeAttribute('data-sticky-buy-bound');
          stickyBuyControllers.delete(root);
        }
      });
      updateMeasurements();
    });
  }

  destroyStickyProductBuy(container) {
    const roots = container.matches?.('[data-product-root]')
      ? [container]
      : Array.from(container.querySelectorAll('[data-product-root]'));

    roots.forEach((root) => stickyBuyControllers.get(root)?.destroy());
  }

  bindOptionPills(container = document) {
    container.querySelectorAll('.product-option').forEach((option) => {
      if (option.dataset.optionPillsBound === 'true') return;
      const select = option.querySelector('[data-option-selector]');
      const pills = Array.from(option.querySelectorAll('[data-option-pill]'));
      if (!select || !pills.length) return;
      option.dataset.optionPillsBound = 'true';
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');

      const updatePills = () => {
        pills.forEach((pill) => {
          const selected = pill.dataset.optionValue === select.value;
          pill.classList.toggle('is-selected', selected);
          pill.setAttribute('aria-pressed', String(selected));
        });
      };

      pills.forEach((pill) => {
        pill.addEventListener('click', () => {
          if (pill.disabled) return;
          select.value = pill.dataset.optionValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          updatePills();
        });
      });

      select.addEventListener('change', updatePills);
      updatePills();
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.yevoriEnhancements = new YevoriEnhancements();
});

document.addEventListener('shopify:section:load', (event) => {
  if (!window.yevoriEnhancements) {
    window.yevoriEnhancements = new YevoriEnhancements();
    return;
  }
  window.yevoriEnhancements.bindStickyProductBuy(event.target);
  window.yevoriEnhancements.bindOptionPills(event.target);
  window.yevoriEnhancements.bindPredictiveSearch();
  window.yevoriEnhancements.bindFormFeedback();
  window.yevoriEnhancements.bindFirstOrderPopup();
});

document.addEventListener('shopify:section:unload', (event) => {
  window.yevoriEnhancements?.destroyStickyProductBuy(event.target);
});
